import { useEffect, useState } from "react";
import { API, errMsg, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY = { type: "pelanggan", name: "", phone: "", email: "", address: "", notes: "" };

export default function Crm() {
  const [tab, setTab] = useState("pelanggan");
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = () => {
    API.get("/crm/contacts").then((r) => setContacts(r.data)).catch(() => {});
    API.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await API.put(`/crm/contacts/${editId}`, form);
      else await API.post("/crm/contacts", form);
      toast.success("Kontak disimpan");
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus kontak ini?")) return;
    await API.delete(`/crm/contacts/${id}`);
    toast.success("Kontak dihapus");
    load();
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";
  const filtered = contacts.filter((c) => c.type === tab);
  const customers = users.filter((u) => u.role === "customer");

  return (
    <div className="space-y-6" data-testid="admin-crm">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Database Pelanggan &amp; Supplier</h1>
          <p className="text-sm text-slate-500">Kontak, relasi bisnis, dan akun pelanggan terdaftar</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY, type: tab === "supplier" ? "supplier" : "pelanggan" }); setEditId(null); }} data-testid="add-contact" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
          <Plus className="h-4 w-4" /> Tambah Kontak
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[{ v: "pelanggan", l: "Pelanggan" }, { v: "supplier", l: "Supplier" }, { v: "akun", l: `Akun Terdaftar (${customers.length})` }].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`crm-tab-${t.v}`} className={`px-5 py-2 text-xs font-bold rounded-full border transition-colors ${tab === t.v ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-500 hover:border-[#0B1F3A]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "akun" ? (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
                <th className="px-5 py-3">Nama</th>
                <th className="px-5 py-3">Username</th>
                <th className="px-5 py-3">Telepon</th>
                <th className="px-5 py-3">Terdaftar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {customers.map((u) => (
                <tr key={u.id}>
                  <td className="px-5 py-3 font-semibold">{u.name}</td>
                  <td className="px-5 py-3 text-slate-500">@{u.username}</td>
                  <td className="px-5 py-3">{u.phone}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
              {customers.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">Belum ada pelanggan terdaftar.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
                <th className="px-5 py-3">Nama</th>
                <th className="px-5 py-3">Telepon</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Catatan</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`contact-row-${c.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-semibold">{c.name}<span className="block text-xs text-slate-400">{c.address}</span></td>
                  <td className="px-5 py-3">{c.phone || "-"}</td>
                  <td className="px-5 py-3">{c.email || "-"}</td>
                  <td className="px-5 py-3 text-xs text-slate-500 max-w-[200px] truncate">{c.notes || "-"}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <button onClick={() => { setForm({ type: c.type, name: c.name, phone: c.phone || "", email: c.email || "", address: c.address || "", notes: c.notes || "" }); setEditId(c.id); }} data-testid={`edit-contact-${c.id}`} className="p-1.5 text-slate-400 hover:text-[#0B1F3A]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(c.id)} data-testid={`delete-contact-${c.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">Belum ada kontak.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-md p-6" data-testid="contact-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Kontak" : "Tambah Kontak"}</h2>
              <button onClick={() => setForm(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Tipe</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="contact-type" className={inputCls}>
                  <option value="pelanggan">Pelanggan</option>
                  <option value="supplier">Supplier</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Nama *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telepon</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Alamat</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="contact-address-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Catatan</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes-input" className={inputCls} />
              </div>
              <button onClick={save} disabled={!form.name} data-testid="save-contact" className="w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
